// The Routing tab: what answers when someone dials this number.
//
// Kevin, 2026-08-26: "we need to really think about how to organize this so it stays simple and easy
// to understand and manage. This tab has the potential to become overwhelming."
//
// ==================================================================================================
// THE ORGANISING PRINCIPLE: ONE DESTINATION, THEN WHAT IS INSIDE IT
// ==================================================================================================
//
// Everything on this tab is one of three things, and saying which keeps it from becoming a pile:
//
//   1. THE DESTINATION — what the inbound route points at. There is exactly ONE. Extension, ring
//      group, auto attendant, queue, time condition. This is the question the tab answers.
//   2. WHAT IS INSIDE IT — the members of that destination. A ring group has extensions and
//      external numbers; an auto attendant has prompts and keys; a single extension has nothing.
//   3. WHAT WE OBSERVE — the PBX's own answer, when it disagrees with ours. Drift, not management.
//
// SIMPLICITY COMES FROM LABELLING, NOT FROM HIDING. That is a correction, and the mistake is worth
// keeping because it was a tempting one.
//
// The first version of this file hid the Extensions and external-numbers cards whenever the
// destination was an auto attendant, reasoning that a menu does not ring extensions. That is false:
// Bud Depot's menu rings extension 13034441564 on key 1. The line has phones no matter what answers
// FIRST, and hiding them meant an operator managing that line could not see or change the extension
// that actually takes the call — and, worse, could not tell whether one existed. A card that is
// absent and a card that is empty look identical, and only one of them is true.
//
// So every card that describes the line is present once the destination is chosen, and each one says
// for itself when it does not currently take effect. An operator can always find a thing and always
// learn why it is not doing anything, which is what "easy to manage" actually requires.
//
// Before this, both were stacked unconditionally and the two newest things were buried: the auto
// attendant lived inside the drift card (whose job is "is the PBX doing what we think", a question
// about US, not about the customer's menu), and External Numbers was a section inside the
// extensions table, which made that card the place you manage something that is not an extension.
//
// WHY EXTERNAL NUMBERS ARE NOT A DESTINATION, even though they now have their own card. A line has
// exactly one destination. Those numbers are MEMBERS of the ring group the destination names — if
// they were a second destination, "what answers when someone dials this number" would stop having
// one answer, which is the question this tab exists to answer. Equal billing with Extensions, yes.
// A peer of the destination, no.

import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import LiveRouteCard from './LiveRouteCard.jsx';
import RouteTypeCard from './RouteTypeCard.jsx';
import LineExtensions from './LineExtensions.jsx';
import ExternalRingCard from './ExternalRingCard.jsx';

export default function RoutingTab({ line, canEdit, onSaved, onChooseDevice }) {
  // Shared with LiveRouteCard by query key, so this is one request rather than two.
  const { data } = useQuery({
    queryKey: ['line', String(line.id), 'route-target'],
    queryFn: () => api.get(`/lines/${line.id}/route-target`),
    retry: false,
  });

  // The customer's own recordings are fetched by the card that needs them now — the attendant rows
  // live inside RouteTypeCard, so a list fetched here would have no reader.

  // LIVE, not `line.routeType`. That column is what Verso would BUILD; a number pointed at a menu
  // by hand still reads `extension` there, which is exactly the case these cards exist for.
  const live = data?.kind ?? null;
  // A ring group either because Verso built one, or because the host is running one. Not used to
  // HIDE the external-numbers card any more — only to tell it whether those numbers ring today.
  const ringsSeveral = live === 'ring-group' || line.routeType === 'ring_group';
  // Until an operator has said how calls reach this line, the members of a route that does not exist
  // yet are not a question worth asking (Kevin, 2026-08-11).
  const chosen = line.routeChosen || line.multiExtension === false;

  return (
    <>
      {/* 3. WHAT WE OBSERVE. Renders nothing when Verso built the route, and nothing about the menu
             itself any more — that moved to its own card below where it belongs. */}
      <LiveRouteCard line={line} />

      {/* 1. THE DESTINATION, and everything specific to it.
             ONE CARD, ONE GEAR. RouteTypeCard owns the gear that sets the route and now carries the
             rows for whatever answers — ring-group strategy and members, or a menu's greeting, keys
             and timeout. I briefly built a second card for the menu and then suppressed this one to
             hide the duplication, which took the gear with it and left no way to set the route
             (Kevin, 2026-08-27: "the gear is gone. how do you set the route?"). This file's own
             comment already warned about it: a separate card "gave the operator two gears for one
             decision — the duplication this page keeps regrowing". */}
      <RouteTypeCard line={line} canEdit={canEdit} onSaved={onSaved} />

      {chosen && (
        <LineExtensions line={line} canEdit={canEdit} onChooseDevice={onChooseDevice} />
      )}
      {chosen && (
        <ExternalRingCard line={line} canEdit={canEdit} ringsToday={ringsSeveral} />
      )}

    </>
  );
}
